
  import useVuelidate from "@vuelidate/core";
  import {helpers, required} from "@vuelidate/validators";
  import {computed, defineComponent, onMounted, onUnmounted, reactive, Ref, toRefs} from "vue";
  import moment from "moment";
  import PerDiem from "@/model/PerDiem";
  import PerDiemDetailsDialogParameters from "@/views/PerDiem/PerDiemDetailsDialogParameters";
  import EmployeeName from "@/model/EmployeeName";
  import OrderName from "@/model/OrderName";
  import {MessageTypes} from "@/components/message-types";
  import OrderServiceV2, {OrderPerDiemsBulkRequest} from "@/service/OrderServiceV2";
  import EmployeeService, {EmployeePerDiemV3UpdateRequest} from "@/service/EmployeeService";
  import {AxiosError} from "axios";
  import eventBus from "@/eventbus";
  import {isAuthorized} from "@/mixin/mixins";
  import store from "@/store/store";

  export default defineComponent({
    name: "PerDiemDetailsDialog",
    props: {
      value: {
        type: Boolean,
        default: false
      }
    },
    setup(props, context) {

      const initialState = {
        availableOrders: new Array<OrderName>(),
        dialog: false,
        employees: new Array<EmployeeName>(),
        orderEditable: true,
        perDiem: new PerDiem(),
        perDiemEditable: true,
        saveForOrder: false,
        serverErrors: new Map<string, string>()
      };

      const state = reactive({...initialState});

      const rules = computed(() => ({
        perDiem: {
          breakfast: {
            chosen: helpers.withMessage("Frühstück ist erforderlich.", (value: boolean | null | undefined) => value !== null),
          },
          catering: {
            required: helpers.withMessage("Verpflegungszuschuss ist erforderlich.", required),
          },
          date: {
            required: helpers.withMessage("Datum ist erforderlich.", required),
          },
          misc: {
            required: helpers.withMessage("Sonstiger Zuschuss ist erforderlich.", required),
          },
          order: {
            required: helpers.withMessage("Auftrag ist erforderlich.", required),
          }
        }
      }));

      // @ts-ignore
      const v$ = useVuelidate(rules, state);

      const errors = computed(() => {
        const errors = new Map<string, string | Ref<string>>();
        if (state.serverErrors.size > 0) {
          return state.serverErrors;
        } else {
          if (v$.value.$dirty) {
            if (v$.value.perDiem.breakfast.$error) {
              errors.set("breakfast", v$.value.perDiem.breakfast.$errors[0].$message);
            }
            if (v$.value.perDiem.catering.$error) {
              errors.set("catering", v$.value.perDiem.catering.$errors[0].$message);
            }
            if (v$.value.perDiem.misc.$error) {
              errors.set("misc", v$.value.perDiem.misc.$errors[0].$message);
            }
            if (v$.value.perDiem.date.$error) {
              errors.set("date", v$.value.perDiem.date.$errors[0].$message);
            }
            if (v$.value.perDiem.order.$error) {
              errors.set("order", v$.value.perDiem.order.$errors[0].$message);
            }
          }
        }

        return errors;
      });

      const disabled = computed(() => {
        if (!isAuthorized("PerDiem_Edit_Edit")) {
          return true;
        } else if (state.perDiem && state.perDiem.uuid) {
          const date = state.perDiem.date;
          return moment(date).isBefore(moment(store.state.general.earliestValidFrom)) || !state.perDiemEditable;
        } else {
          return false;
        }
      });

      const cateringPerDiems = computed(() => [0, 8, 14, 32]);

      const miscPerDiems = computed(() => [0, 2, 8, 16, 24, 32]);

      const cancel = () => {
        state.dialog = false;
      };

      const save = () => {
        state.serverErrors = new Map<string, string>();
        v$.value.$reset();
        v$.value.$touch();

        if (!v$.value.$invalid) {
          // The select sets the order's uuid, while a preset order is the order itself
          const selected = state.perDiem.order as OrderName | string;
          const orderUuid = typeof selected === "string" ? selected : selected.uuid as string;

          if (state.saveForOrder) {
            saveOrderPerDiems(orderUuid);
          } else if (state.employees.length === 1) {
            savePerDiemV3(state.employees[0].uuid, orderUuid);
          }
        }
      };

      /**
       * Gives all employees of the dialog the per diems on its day via the order's v2 API. Clearing both amounts and
       * breakfast deletes their per diems on that day.
       */
      const saveOrderPerDiems = (orderUuid: string) => {
        const request: OrderPerDiemsBulkRequest = {
          date: state.perDiem.date,
          employeeIds: state.employees.map(employee => employee.uuid),
          catering: state.perDiem.catering as number,
          misc: state.perDiem.misc as number,
          breakfast: !!state.perDiem.breakfast
        };

        eventBus.$emit(MessageTypes.LOADING, "Setze Zuschüsse...");
        OrderServiceV2.updateOrderPerDiems(orderUuid, request)
          .then(() => {
            eventBus.$emit(MessageTypes.SUCCESS, "Zuschüsse gesetzt!");
            eventBus.$emit(MessageTypes.PERDIEM_UPDATED);
            state.dialog = false;
          })
          .catch(warnIfRejected);
      };

      /**
       * Creates, updates or deletes the employee's per diem via the v3 API. Clearing both amounts and breakfast
       * deletes an existing per diem, and records nothing for a day without one.
       */
      const savePerDiemV3 = (employeeUuid: string, orderUuid: string) => {
        const request: EmployeePerDiemV3UpdateRequest = {
          orderId: orderUuid,
          catering: state.perDiem.catering as number,
          misc: state.perDiem.misc as number,
          breakfast: !!state.perDiem.breakfast
        };
        const empty = Number(request.catering) === 0 && Number(request.misc) === 0 && !request.breakfast;
        const perDiemId = state.perDiem.uuid;

        let saved: Promise<unknown>;
        if (perDiemId) {
          saved = empty
            ? EmployeeService.deletePerDiemV3(employeeUuid, perDiemId)
            : EmployeeService.updatePerDiemV3(employeeUuid, perDiemId, request);
        } else if (empty) {
          state.dialog = false;
          return;
        } else {
          saved = EmployeeService.createPerDiemV3(employeeUuid, {...request, date: state.perDiem.date});
        }

        eventBus.$emit(MessageTypes.LOADING, "Setze Zuschüsse...");
        saved
          .then(() => {
            eventBus.$emit(MessageTypes.SUCCESS, "Zuschüsse gesetzt!");
            eventBus.$emit(MessageTypes.PERDIEM_UPDATED);
            state.dialog = false;
          })
          .catch(warnIfRejected);
      };

      const warnIfRejected = (error: AxiosError) => {
        const status = error?.response?.status;
        if (status === 409 || status === 422) {
          eventBus.$emit(MessageTypes.WARNING, "Zuschüsse konnten nicht gesetzt werden!");
        }
      };

      const openDialogCallback = (params: PerDiemDetailsDialogParameters) => {
        if (!state.dialog) {
          v$.value.$reset();
          state.perDiemEditable = true;

          state.availableOrders = params.orders;
          state.orderEditable = state.availableOrders.length > 1;

          state.employees = params.employees;
          state.saveForOrder = params.saveForOrder;

          if (params.perDiem) {
            state.perDiem = Object.assign(new PerDiem(), params.perDiem);
            const orderAlreadyAdded = state.availableOrders.some(order => params.perDiem.order && order.uuid === params.perDiem.order.uuid);
            if (!orderAlreadyAdded) {
              // The order was not available but set, because it was assigned somewhere else. So this
              // one should not be editable anymore
              state.perDiemEditable = false;
              state.availableOrders.push(params.perDiem.order);
            }
          } else {
            state.perDiem = new PerDiem();
            state.perDiem.date = params.date;
            if (state.employees.length === 1) {
              state.perDiem.employee = state.employees[0];
            }

            if (state.availableOrders.length === 1) {
              state.perDiem.order = state.availableOrders[0];
            }
          }

          state.dialog = true;
        }
      };

      onMounted(() => {
        eventBus.$on(MessageTypes.OPEN_PERDIEM_DETAILS, openDialogCallback);
      });

      onUnmounted(() => {
        eventBus.$off(MessageTypes.OPEN_PERDIEM_DETAILS, openDialogCallback);
      });

      return {
        ...toRefs(state),
        v$,
        errors,
        disabled,
        cateringPerDiems,
        miscPerDiems,
        cancel,
        save
      };
    }
  });
